import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  ButtonLink,
  Column,
  Columns,
  Heading,
  Stack,
  Text,
} from 'braid-design-system';
import { useEffect } from 'react';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';

import { PoweredBySeekPassFooter } from '../PoweredBySeekPassFooter';
import { useVerificationDialog } from '../VerificationDialogs/useVerificationDialog';
import { verifiableCredentialTypenameToCertsyType } from '../analyticsUtils';
import { useVerifications } from '../data';
import type { VerifiableCredential } from '../types';

import translations from './.vocab';
import { TrophySvg } from './TrophySvg';

const VerifiableCredentialItem = ({
  credential,
}: {
  credential: VerifiableCredential;
}) => {
  const { t } = useTranslations(translations);

  const { seekPassVerifyClicked } = useAnalytics();

  const { label, url, __typename } = credential;

  const { dialog, setShowDialog } = useVerificationDialog({
    typename: __typename,
    url,
    dialogTitle:
      'verificationMessage' in credential &&
      credential.verificationMessage !== null
        ? credential.verificationMessage
        : '',
    origin: 'available verifications',
  });

  const verifyButtonOnClick = () => {
    const certsyType = verifiableCredentialTypenameToCertsyType[__typename];
    seekPassVerifyClicked({ certsyType });

    setShowDialog(true);
  };

  return (
    <>
      <Columns space="medium" alignY="center">
        <Column>
          <Text>{label}</Text>
        </Column>
        <Column width="content">
          <Button
            tone="formAccent"
            variant="transparent"
            onClick={verifyButtonOnClick}
            bleed
            data={{ automation: `verify-credential-${credential.__typename}` }}
          >
            {t('Verify')}
          </Button>
        </Column>
      </Columns>
      {dialog}
    </>
  );
};

export const AvailableVerifications = () => {
  const { t } = useTranslations(translations);

  const { verificationsDrawerOpened, seekPassInReviewClicked } = useAnalytics();

  const { verifiable, verificationsTrackingProps } = useVerifications();

  useEffect(() => {
    const { credentialsNotVerifiedTotal, credentialsNotVerifiedType } =
      verificationsTrackingProps;

    verificationsDrawerOpened({
      credentialsNotVerifiedTotal,
      credentialsNotVerifiedType,
      currentPageSubsection: 'available verifications',
    });
    // Only send tracking once on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emptyContent = (
    <Stack space="medium" align="center">
      <TrophySvg />
      <Heading level="4" align="center">
        {t('Great work, all available information verified!')}
      </Heading>
      <Text tone="secondary">
        {t('We will let you know when there are more verifications available.')}
      </Text>
    </Stack>
  );

  const pendingStatus = (count: number) => (
    <>
      <Columns space="medium" alignY="center">
        <Column>
          <Text>
            {t('In review')} ({count})
          </Text>
        </Column>
        <Column width="content">
          <ButtonLink
            tone="formAccent"
            variant="transparent"
            onClick={() => seekPassInReviewClicked()}
            href="https://app.seekpass.co/career-passport/recommended"
            target="_blank"
            bleed
          >
            {t('Go to SEEK Pass')}
          </ButtonLink>
        </Column>
      </Columns>
    </>
  );

  return (
    <Stack space="none">
      {verifiable && verifiable.length > 0 ? (
        <Stack space="xlarge">
          {verifiable.map(
            ({ label, credentials, pendingCount }, sectionIndex) => (
              <Stack space="large" key={sectionIndex}>
                <Text weight="strong">{label}</Text>
                <Stack space="xlarge">
                  {credentials
                    ? credentials.map((credential, credentialIndex) => (
                        <VerifiableCredentialItem
                          key={credentialIndex}
                          credential={credential}
                        />
                      ))
                    : null}
                  {pendingCount ? pendingStatus(pendingCount) : null}
                </Stack>
              </Stack>
            ),
          )}
        </Stack>
      ) : (
        emptyContent
      )}
      <Box paddingBottom="xlarge" />
      <PoweredBySeekPassFooter />
    </Stack>
  );
};
